import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { authManager } from '../utils/auth';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

const defaultSocialLinks = {
    facebook: '',
    twitter: '',
    linkedin: '',
    pinterest: '',
    youtube: '',
    instagram: '',
    threads: ''
};

const Brand_Edit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);

    const [formData, setFormData] = useState({
        name: '',
        brandLogo: '',
        network: '',
        description: '',
        address: '',
        phoneNumber: '',
        email: '',
        socialLinks: { ...defaultSocialLinks }
    });

    const [logoFile, setLogoFile] = useState(null);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });

        const fetchBrand = async () => {
            try {
                const res = await authManager.authenticatedFetch(`${Base_Url}/shopping/brands/${id}`);
                const data = await res.json();
                const result = data.result || {};

                setFormData({
                    name: result.name || '',
                    brandLogo: result.brandLogo || '',
                    network: result.network || '',
                    description: result.description || '',
                    address: result.address || '',
                    phoneNumber: result.phoneNumber || '',
                    email: result.email || '',
                    socialLinks: {
                        ...defaultSocialLinks,
                        ...(result.socialLinks || result.socialmedia || {})
                    }
                });
            } catch (error) {
                console.error('Error fetching brand:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchBrand();
    }, [id]);

    const uploadImageToS3 = async (file) => {
        try {
            const payload = {
                fileName: file.name,
                fileType: file.type,
                folder: 'brands/logos/'
            };

            const res = await authManager.authenticatedFetch(`${Base_Url}/shopping/brands/upload/presign`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await res.json();

            if (!res.ok || !data.presignedUrl || !data.fileUrl) {
                console.error('Presign error response:', data);
                throw new Error(data.message || 'Failed to generate presigned URL');
            }

            const { presignedUrl, fileUrl } = data;

            const upload = await fetch(presignedUrl, {
                method: 'PUT',
                headers: { 'Content-Type': file.type },
                body: file
            });

            if (!upload.ok) throw new Error('Image upload to S3 failed');

            return fileUrl;
        } catch (err) {
            console.error('Image upload error:', err);
            throw err;
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name in defaultSocialLinks) {
            setFormData((prev) => ({
                ...prev,
                socialLinks: {
                    ...prev.socialLinks,
                    [name]: value
                }
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            setFormData((prev) => ({
                ...prev,
                brandLogo: URL.createObjectURL(file)
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            let finalLogoUrl = formData.brandLogo;

            if (logoFile) {
                finalLogoUrl = await uploadImageToS3(logoFile);
            }

            const cleanSocialLinks = {};
            Object.entries(formData.socialLinks).forEach(([key, val]) => {
                cleanSocialLinks[key] = val?.trim?.() || '';
            });

            const payload = {
                name: formData.name,
                network: formData.network,
                description: formData.description,
                address: formData.address?.trim() || '',
                phoneNumber: formData.phoneNumber?.trim() || '',
                email: formData.email?.trim() || '',
                brandLogo: finalLogoUrl,
                socialLinks: cleanSocialLinks
            };

            const res = await authManager.authenticatedFetch(`${Base_Url}/shopping/brands/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await res.json();

            if (res.ok) {
                toast.success('Brand updated successfully!');
                setTimeout(() => navigate('/brands'), 3000);
            } else {
                toast.error(result.message || 'Update failed');
            }
        } catch (err) {
            console.error('Error updating brand:', err);
            toast.error('Error updating brand.');
        }
    };

    return (
        <div>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2 align-items-center">
                        <div className="col-6 col-sm-6">
                            <h1 className="m-0 txt_lft">Brands</h1>
                        </div>
                        <div className="col-6 col-sm-6">
                            <ol className="breadcrumb justify-content-end">
                                <li className="breadcrumb-item">
                                    <Link to="/brands">
                                        <i className="fa-solid fa-arrow-left-long" /> Back
                                    </Link>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="frmwdth_cntr">
                            <div className="col-lg-12">
                                <div className="card card-primary card-outline">
                                    {loading ? (
                                        <div className="card-body text-center">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <form className="form-horizontal" onSubmit={handleSubmit}>
                                            <div className="card-body">
                                                {/* All fields */}
                                                {[
                                                    { label: 'Name', name: 'name', type: 'text' },
                                                    { label: 'Description', name: 'description', type: 'textarea' },
                                                    { label: 'Address', name: 'address', type: 'text' },
                                                    { label: 'Phone', name: 'phoneNumber', type: 'text' },
                                                    { label: 'Email', name: 'email', type: 'email' }
                                                ].map(({ label, name, type }) => (
                                                    <div className="form-group row" key={name}>
                                                        <label className="col-md-4 col-form-label">{label}</label>
                                                        <div className="col-md-8">
                                                            {type === 'textarea' ? (
                                                                <textarea name={name} className="form-control" value={formData[name]} onChange={handleChange} />
                                                            ) : (
                                                                <input type={type} name={name} className="form-control" value={formData[name] || ''} onChange={handleChange} />
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}

                                                {/* Logo upload */}
                                                <div className="form-group row">
                                                    <label className="col-md-4 col-form-label">Logo</label>
                                                    <div className="col-md-8">
                                                        <div className="s_profileupld mb-2">
                                                            <label className="btn-bs-file btn-blue">
                                                                Add Logo
                                                                <input type="file" accept="image/*" onChange={handleLogoChange} />
                                                            </label>
                                                        </div>
                                                        {formData.brandLogo && (
                                                            <div className="d-flex flex-row flex-wrap pt-20 pb-15">
                                                                <div className="community_imgwrapper">
                                                                    <div className="community_icon">
                                                                        <img src={formData.brandLogo} alt="Brand" />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Affiliate Platform */}
                                                <div className="form-group row">
                                                    <label className="col-md-4 col-form-label">Affiliate Platform</label>
                                                    <div className="col-md-8">
                                                        <select name="network" className="form-control" value={formData.network} onChange={handleChange}>
                                                            <option value="">Select Platform</option>
                                                            <option value="CJ">CJ</option>
                                                            <option value="IMPACT">IMPACT</option>
                                                            <option value="Rakuten">Rakuten</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                {/* Social Media */}
                                                <div className="form-group row">
                                                    <label className="col-md-4 col-form-label">Social Media</label>
                                                    <div className="col-md-8">
                                                        {Object.entries(formData.socialLinks).map(([platform, value]) => (
                                                            <div className="input-group mb-3" key={platform}>
                                                                <div className="input-group-prepend">
                                                                    <div className="input-group-text">
                                                                        <i className={`fa-brands fa-${platform === 'twitter' ? 'x-twitter' : platform}`}></i>
                                                                    </div>
                                                                </div>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    name={platform}
                                                                    value={value || ''}
                                                                    onChange={handleChange}
                                                                    placeholder={`Enter ${platform} link`}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="card-footer text-center">
                                                <button type="submit" className="btn btn-blue">
                                                    Update
                                                </button>
                                            </div>
                                        </form>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Brand_Edit;
